// Consolidated Ordergroove API: All API functions are inlined here and exported.
// Other files now import from this file instead of ordergroove-api.js, eliminating
// the need for a separate API file and saving HTTP requests.

// Ordergroove API functions
const baseOrdergrooveUrl = "https://restapi.ordergroove.com";
const baseCloudflareUrl = `https://${window.barkShopDomain}/api/ordergroove`
// GraphQL versions are YYYY-MM. Pinned deliberately — "unstable" tracks the latest schema,
// which would break silently inside a cached theme asset.
const graphqlVersion = "2026-01";

// bark.co domains proxy Ordergroove through Cloudflare; everything else talks to OG directly.
function resolveBaseUrl() {
  return ['dev.bark.co', 'stg.bark.co', 'bark.co'].includes(window.barkShopDomain) ? baseCloudflareUrl : baseOrdergrooveUrl;
}

function getHeaders(tokenHash) {
  const headers = {
    "Content-Type": "application/json",
  };

  if (tokenHash) {
    headers['Authorization'] = JSON.stringify(tokenHash);
  }

  return headers;
}

async function ogApiCall(method, url, body=null, tokenHash=null) {
  const ogApiUrl = resolveBaseUrl() + url;
  const headers = getHeaders(tokenHash);

  const options = {
    method,
    headers,
    credentials: 'same-origin'
  };

  if (body !== null) {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(ogApiUrl, options);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  return { data };
}

function ogApiGet(url, tokenHash=null) {
  return ogApiCall('GET', url, null, tokenHash);
}

function ogApiPost(url, body, tokenHash=null) {
  return ogApiCall('POST', url, body, tokenHash);
}

function ogApiPatch(url, body, tokenHash=null) {
  return ogApiCall('PATCH', url, body, tokenHash);
}

function buildTokenHash() {
  const ogSigDataDiv = document.querySelector(".og-sig-data");
  if (!ogSigDataDiv) {
    throw new Error("ATB Module: ogSigDataDiv not found");
  }
  const customerId = ogSigDataDiv.getAttribute('data-attr-customer-id');
  if (customerId) {
    const ts = ogSigDataDiv.getAttribute('data-attr-ts');
    const hmac_sha = ogSigDataDiv.getAttribute('data-attr-hmac_sha');
    const sig = btoa(String.fromCharCode.apply(null, hmac_sha.match(/\w{2}/g).map(function (a) { return parseInt(a, 16) })));
    const ogMerchantId = ogSigDataDiv.getAttribute('data-attr-og-mid');
    return {
      public_id: ogMerchantId,
      sig_field: customerId,
      ts,
      sig
    };
  } else {
    return null;
  }
}

// Ordergroove's GraphQL API authenticates exactly like the REST API — the same HMAC token
// hash from buildTokenHash(), sent as the Authorization header — so storefront (customer
// scoped) calls need no new credentials.
async function ogGraphqlCall(query, variables=null, tokenHash=null) {
  const ogApiUrl = `${resolveBaseUrl()}/graphql/${graphqlVersion}/`;

  const response = await fetch(ogApiUrl, {
    method: 'POST',
    headers: getHeaders(tokenHash),
    credentials: 'same-origin',
    body: JSON.stringify({ query, variables })
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const payload = await response.json();

  // GraphQL reports failures as a 200 with an errors array, so response.ok isn't sufficient.
  if (payload.errors?.length) {
    throw new Error(payload.errors[0]?.message || 'Ordergroove GraphQL error');
  }

  return payload.data;
}

async function getSubscriptions() {
  try {
    const tokenHash = buildTokenHash();
    const response = await ogApiGet('/subscriptions/?page_size=100&live=true', tokenHash);

    return response.data.results || [];
  } catch (error) {
    console.error('Error fetching subscriptions:', error);
    return [];
  }
}

// GraphQL returns extra_data as a JSON string where REST hands back a parsed object.
function parseExtraData(extraData) {
  if (!extraData) return {};

  try {
    return typeof extraData === 'string' ? JSON.parse(extraData) : extraData;
  } catch (e) {
    return {}; // malformed blob is treated as absent, matching isPlaySubscriptionProduct
  }
}

const subscriptionsWithProductsQuery = `
  query SubscriptionsWithProducts($customer: String!, $first: Int!) {
    subscriptions(customer: $customer, live: true, first: $first) {
      edges {
        node {
          publicId
          extraData
          product {
            name
            externalProductId
          }
        }
      }
      pageInfo {
        hasNextPage
      }
    }
  }
`;

/**
 * Single-request replacement for getSubscriptions() plus a batched /products/ lookup: the
 * GraphQL subscriptions connection nests each subscription's product inline, so the product
 * name arrives with the subscription instead of costing a second round trip.
 *
 * Results are normalized to snake_case so callers read the same shape the REST helpers use.
 * Unlike getSubscriptions(), this throws rather than returning [] — callers need to tell a
 * genuine failure from "no subscriptions" so they can fall back to the REST path.
 */
async function getSubscriptionsWithProducts(customerId, pageSize=100) {
  const tokenHash = buildTokenHash();
  const data = await ogGraphqlCall(
    subscriptionsWithProductsQuery,
    { customer: customerId, first: pageSize },
    tokenHash
  );

  const connection = data?.subscriptions;
  if (connection?.pageInfo?.hasNextPage) {
    console.warn(`Ordergroove: more than ${pageSize} live subscriptions, results truncated.`);
  }

  return (connection?.edges || []).map(({ node }) => ({
    public_id: node?.publicId,
    product_name: node?.product?.name || '',
    external_product_id: node?.product?.externalProductId,
    commitment: parseExtraData(node?.extraData).commitment || null
  }));
}

function isPlaySubscriptionProduct(product) {
  // extra_data can be null or a json string; a malformed blob reads as not-Play
  const productType = parseExtraData(product?.extra_data).product_type?.toLowerCase() || "";
  return productType === "play";
}

async function playSubscriber() {
  try {
    const subscriptions = await getSubscriptions();
    if (subscriptions.length === 0) {
      return false;
    }

    const productParams = subscriptions.map(sub => `external_product_ids[]=${sub.product}`).join('&');
    const subscriptionProductsResponse = await ogApiGet(`/products/?page_size=100&${productParams}`);
    const subscriptionProducts = subscriptionProductsResponse.data?.results || [];

    return subscriptionProducts.some(product => isPlaySubscriptionProduct(product));
  } catch (error) {
    console.error('Error checking Play subscription status:', error);
    return false;
  }
}

/**
 * Resolve Ordergroove line item id for a subscription. Change-product API requires item_id (line item), not subscription id.
 * Uses subscription filter so it works for both non-swapped and theme-swapped subs (one call per sub, no extra fallback calls).
 */
async function fetchItemIdForSubscription(subscription, tokenHash) {
  try {
    const subPublicId = subscription.public_id;
    const productId = subscription.product;
    const response = await ogApiGet(
      `/items/?subscription=${subPublicId}&omit_price_calculation=true&status=1&page_size=50`,
      tokenHash
    );
    const items = response.data?.results || [];
    if (items.length === 0) return null;
    const match = items.find((i) => String(i.product || '') === String(productId || ''));
    return (match || items[0]).public_id;
  } catch (err) {
    return null;
  }
}

export { ogApiGet, ogApiPost, ogApiPatch, ogGraphqlCall, buildTokenHash, getSubscriptions, getSubscriptionsWithProducts, playSubscriber, fetchItemIdForSubscription };

document.addEventListener("ognextorderfetched", async (e) => {
  const nextOrder = e.detail.nextOrder;
  const tokenHash = e.detail.tokenHash;
  const items = await ogApiGet(
    `/items/?page_size=100&order=${nextOrder.public_id}`,
    tokenHash
  );

  const atbTotal = items.data.results.reduce(
    (total, item) =>
      total + (!!item.subscription ? 0 : parseFloat(item.total_price) || 0),
    0
  );
  sessionStorage.setItem("og-next-order-atb-total", atbTotal);
  document.dispatchEvent(new CustomEvent("ognextorderitemsfetched", { detail: { items } }));
});

window.addEventListener("DOMContentLoaded", async function () {
  const nextOrder = JSON.parse(sessionStorage.getItem("og-next-order"));
  let nextOrderCustomerId;
  if (nextOrder) {
    nextOrderCustomerId = nextOrder.customer;
  }

  const ogSigDataDiv = document.querySelector(".og-sig-data");
  const loggedInCustomerId = ogSigDataDiv.getAttribute("data-attr-customer-id");

  if (ogSigDataDiv) {
    try {
      const ordersUrl =
        "/orders/?status=1&ordering=place&exclude_prepaid_orders=true&page_size=1";
      const hasBarkSig = cookieContains("bark_signature");
      if (!loggedInCustomerId && !hasBarkSig) return;

      let tokenHash;
      if (loggedInCustomerId) {
        tokenHash = buildTokenHash();

        // next order already in session storage
        try {
          if (window.rudderanalytics) {
            window.rudderanalytics.identify(loggedInCustomerId);
          }
        } catch (identifyError) {
          console.warn(
            "Failed to identify customer in RudderStack:",
            identifyError
          );
        }
      }

      const orders = await ogApiGet(ordersUrl, tokenHash);
      if (orders.data.results.length > 0) {
        const nextOrder = orders.data.results[0];
        sessionStorage.setItem("og-next-order", JSON.stringify(nextOrder));

        document.dispatchEvent(
          new CustomEvent("ognextorderfetched", {
            detail: {
              nextOrder,
              tokenHash,
            },
          })
        );

        // If customer is not logged in and using atbSignature, identify them in RudderStack
        if (
          !loggedInCustomerId &&
          hasBarkSig &&
          nextOrder &&
          nextOrder.customer
        ) {
          try {
            if (window.rudderanalytics) {
              window.rudderanalytics.identify(nextOrder.customer);
            }
          } catch (identifyError) {
            console.warn(
              "Failed to identify customer in RudderStack:",
              identifyError
            );
          }
        }

        document.dispatchEvent(
          new CustomEvent("ognextorderadded", {
            detail: {
              nextOrder,
              tokenHash,
            },
          })
        );
      } else {
        sessionStorage.removeItem("og-next-order");
      }
    } catch (error) {
      console.log(error);
      newrelic.noticeError(error, { loggedInCustomerId });
    }
  }
});
